import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import adminAPI from "./apiClient";
import type { Turn, Action, TurnOutput } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";

type TabId = "overview" | "actions" | "outputs" | "context" | "error";

const validTabs: TabId[] = ["overview", "actions", "outputs", "context", "error"];

export default function TurnDetail(): JSX.Element {
  const { conversationId, turnId } = useParams<{ conversationId: string; turnId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [turn, setTurn] = useState<Turn | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [outputs, setOutputs] = useState<TurnOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize tab from URL or default to "overview"
  const tabFromUrl = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "overview"
  );

  // Sync tab state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const newTab = searchParams.get("tab") as TabId | null;
    if (newTab && validTabs.includes(newTab) && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  // Update URL when tab changes (replace to avoid polluting history)
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    navigate(`?tab=${tab}`, { replace: true });
  };

  useEffect(() => {
    fetchTurnDetails();
  }, [conversationId, turnId]);

  const fetchTurnDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const turnData = await adminAPI.getTurn(conversationId, turnId);

      setTurn(turnData.turn);
      setActions(turnData.actions);
      setOutputs(turnData.outputs);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getFeedbackIcon = (thumbsUp: boolean, thumbsDown: boolean): string => {
    if (thumbsUp) return "👍";
    if (thumbsDown) return "👎";
    return "—";
  };

  const getFeedbackColor = (thumbsUp: boolean, thumbsDown: boolean): string => {
    if (thumbsUp) return "text-green-600";
    if (thumbsDown) return "text-red-600";
    return "text-gray-400";
  };

  const formatJSON = (data: unknown): string => {
    try {
      if (typeof data === 'string') {
        return JSON.stringify(JSON.parse(data), null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!turn) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Turn not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Turn Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Turn Details</h2>
          {error && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
              Using mock data - API not available
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Turn ID</label>
            <p className="mt-1 text-sm font-mono text-gray-900">{turn.turn_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created At</label>
            <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(turn.created_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Actions</label>
            <p className="mt-1 text-sm text-gray-900">
              {actions.length}
              {actions.some(a => a.is_decision) && (
                <span className="ml-2 text-xs text-purple-600" title="Includes agent decision entries">
                  (incl. decisions)
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Outputs</label>
            <p className="mt-1 text-sm text-gray-900">{outputs.length}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Turn Cost</label>
            <div className="mt-1">
              <p className="text-sm font-medium text-gray-900">
                {formatCost(getTotalCostFromApi(
                  turn.total_input_cost_usd,
                  turn.total_output_cost_usd
                ))}
              </p>
              {(turn.total_prompt_tokens || turn.total_completion_tokens) && (
                <p className="text-xs text-gray-500">
                  {((turn.total_prompt_tokens || 0) + (turn.total_completion_tokens || 0)).toLocaleString()} tokens
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-500">User Feedback:</span>
            <span className={`text-lg ${getFeedbackColor(turn.thumbs_up, turn.thumbs_down)}`}>
              {getFeedbackIcon(turn.thumbs_up, turn.thumbs_down)}
            </span>
          </div>
          {turn.feedback_comment && (
            <div className="flex-1">
              <span className="text-sm text-gray-600">"{turn.feedback_comment}"</span>
            </div>
          )}
        </div>

        {/* Error Status */}
        {turn.error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <span className="text-red-600 mr-2">⚠️</span>
              <span className="text-sm font-medium text-red-800">This turn encountered an error during processing</span>
            </div>
            {turn.error_text && (
              <p className="mt-2 text-sm text-red-700">{turn.error_text}</p>
            )}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: "overview", name: "Overview" },
              { id: "actions", name: `Actions (${actions.length})${actions.some(a => a.is_decision) ? ' • incl. decisions' : ''}` },
              { id: "outputs", name: `Outputs (${outputs.length})` },
              { id: "context", name: "Context" },
              ...(turn?.error ? [{ id: "error", name: "Error Details" }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as TabId)}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? (tab.id === "error" ? "border-red-500 text-red-600" : "border-blue-500 text-blue-600")
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.name}
                {tab.id === "error" && <span className="ml-1">⚠️</span>}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* User Input */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">User Input</label>
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-gray-900">{turn.user_input_text}</p>
                </div>
              </div>

              {/* Final Response */}
              {turn.final_chat_response && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Final Chat Response</label>
                  <div className="bg-blue-50 p-4 rounded-md">
                    <p className="text-gray-900 whitespace-pre-wrap">{turn.final_chat_response}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions Tab */}
          {activeTab === "actions" && (
            <div className="space-y-4">
              {actions.map((action) => (
                <div
                  key={action.action_id}
                  className={`border rounded-lg p-4 hover:bg-gray-50 cursor-pointer ${
                    action.is_decision
                      ? 'border-purple-300 bg-purple-50/30'
                      : 'border-gray-200'
                  }`}
                  onClick={() => navigate(`/admin/conversation/${conversationId}/turn/${turnId}/action/${action.action_id}?fromTab=${activeTab}`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="text-lg font-medium text-gray-900">{action.action_type}</h4>
                          {action.is_decision && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              Agent Decision
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{formatDateWithZone(action.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {action.llm_request_count || 0} LLM requests
                      </div>
                      {(action.total_input_cost_usd || action.total_output_cost_usd) && (
                        <div className="text-sm font-medium text-green-700">
                          {formatCost(getTotalCostFromApi(
                            action.total_input_cost_usd,
                            action.total_output_cost_usd
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Decision-specific info */}
                  {action.is_decision && (action.selected_action || action.resolved_action) && (
                    <div className="mb-3 p-3 bg-white border border-purple-200 rounded">
                      <label className="block text-xs font-medium text-purple-700 mb-1">Decision</label>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="font-mono text-purple-900">{action.selected_action}</span>
                        {action.resolved_action && action.resolved_action !== action.selected_action && (
                          <>
                            <span className="text-purple-500">→</span>
                            <span className="font-mono text-purple-900">{action.resolved_action}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {action.rationale && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Rationale</label>
                      <p className="text-sm text-gray-700">{action.rationale}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <a
                      href={`/admin/conversation/${conversationId}/turn/${turnId}/action/${action.action_id}?fromTab=${activeTab}`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View Details →
                    </a>
                  </div>
                </div>
              ))}

              {actions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No actions found for this turn.
                </div>
              )}
            </div>
          )}

          {/* Outputs Tab */}
          {activeTab === "outputs" && (
            <div className="space-y-4">
              {outputs.map((output) => (
                <div key={output.turn_output_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">Output {output.turn_output_id}</h4>
                      <p className="text-sm text-gray-500">Type: {output.output_type} • {formatDateWithZone(output.created_at)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-lg ${getFeedbackColor(output.thumbs_up, output.thumbs_down)}`}>
                        {getFeedbackIcon(output.thumbs_up, output.thumbs_down)}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Output Content</label>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                        {formatJSON(output.output_content)}
                      </pre>
                    </div>
                  </div>

                  {output.feedback_comment && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Feedback</label>
                      <p className="text-sm text-gray-700 italic">"{output.feedback_comment}"</p>
                    </div>
                  )}
                </div>
              ))}

              {outputs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No outputs found for this turn.
                </div>
              )}
            </div>
          )}

          {/* Context Tab */}
          {activeTab === "context" && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Merged Context</label>
              <div className="bg-gray-50 p-4 rounded-md">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                  {formatJSON(turn.merged_context)}
                </pre>
              </div>
            </div>
          )}

          {/* Error Details Tab */}
          {activeTab === "error" && turn.error && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <span className="text-red-600 text-xl mr-2">⚠️</span>
                  <h3 className="text-lg font-medium text-red-800">Error Information</h3>
                </div>

                {turn.error_text && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-red-700 mb-2">Error Message</label>
                    <div className="bg-white p-3 rounded border border-red-200">
                      <p className="text-red-800 font-mono text-sm">{turn.error_text}</p>
                    </div>
                  </div>
                )}

                {turn.error_stack && (
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-2">Stack Trace</label>
                    <div className="bg-white p-3 rounded border border-red-200 max-h-96 overflow-y-auto">
                      <pre className="text-red-800 font-mono text-xs whitespace-pre-wrap">
                        {turn.error_stack}
                      </pre>
                    </div>
                  </div>
                )}

                {!turn.error_text && !turn.error_stack && (
                  <p className="text-red-700">Error flag is set but no error details are available.</p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-amber-600 text-lg mr-2">💡</span>
                  <h4 className="text-amber-800 font-medium">Troubleshooting Tips</h4>
                </div>
                <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                  <li>Check the stack trace for the specific line and function where the error occurred</li>
                  <li>Review the merged context in the Context tab for any unusual input data</li>
                  <li>Look at the actions that were executed before this error occurred</li>
                  <li>Check if this is a recurring error pattern across multiple turns</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}